import React, { useState, useMemo } from 'react';
import Modal from '../shared/Modal';
import Button from '../shared/Button';
import './CardModal.css';

const BulkAddCardsModal = ({ type, onAdd, onClose }) => {
  const [text, setText] = useState('');

  // Count blanks in text (any sequence of underscores = 1 blank)
  const countBlanks = (str) => {
    const matches = str.match(/_+/g);
    return matches ? matches.length : 0;
  };

  // Parse cards from textarea
  const parsedCards = useMemo(() => {
    const lines = text
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (type === 'black') {
      return lines.map((line) => ({
        text: line,
        blanks: countBlanks(line),
      }));
    } else {
      return lines.map((line) => ({
        text: line,
      }));
    }
  }, [text, type]);

  // Validation
  const errors = useMemo(() => {
    const newErrors = [];

    parsedCards.forEach((card, index) => {
      if (type === 'black') {
        if (card.text.length > 200) {
          newErrors.push(`Line ${index + 1}: Too long (max 200 characters)`);
        }
      } else {
        if (card.text.length > 100) {
          newErrors.push(`Line ${index + 1}: Too long (max 100 characters)`);
        }
      }
    });

    return newErrors;
  }, [parsedCards, type]);

  // Handle submit
  const handleSubmit = () => {
    if (parsedCards.length === 0) {
      return;
    }

    if (errors.length > 0) {
      return;
    }

    onAdd(parsedCards);
  };

  const cardTypeName = type === 'black' ? 'Question' : 'Answer';

  const footer = (
    <>
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSubmit}
        disabled={parsedCards.length === 0 || errors.length > 0}
      >
        Add {parsedCards.length} Card{parsedCards.length !== 1 ? 's' : ''}
      </Button>
    </>
  );

  return (
    <Modal
      show={true}
      onClose={onClose}
      title={`Add ${cardTypeName} Cards`}
      footer={footer}
      size="lg"
      contentClassName="card-modal bulk-add-modal"
    >
      {type === 'black' && (
        <div className="info-box">
          <strong>💡 How blanks work:</strong>
          <ul>
            <li>
              Use underscores <code>_</code> for blanks in question cards (any amount works!)
            </li>
            <li>Example: "Why did _ cross the road?" = 1 blank</li>
            <li>Example: "_ and _ are perfect together" = 2 blanks</li>
            <li>Example: "_, _, and _" = 3 blanks</li>
            <li>No blanks? That's fine too! "What is your name?" works as-is</li>
          </ul>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="bulk-text">Paste cards below (one per line):</label>
        <textarea
          id="bulk-text"
          className="textarea-field bulk-textarea"
          placeholder={
            type === 'black'
              ? 'a/s/l?\nWhat is your name?\nErmagerd??\nZoink?'
              : 'Jupiter\nHuman caterpillar\nDexter Morgan\nA confused platypus'
          }
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={15}
          autoFocus
        />
        <div className="bulk-info">
          <div className="cards-found">
            {parsedCards.length > 0 ? (
              <>
                ✅ <strong>{parsedCards.length}</strong> card
                {parsedCards.length !== 1 ? 's' : ''} found
              </>
            ) : (
              <>⚠️ No cards found - add one card per line</>
            )}
          </div>
          {type === 'black' && parsedCards.length > 0 && (
            <div className="blank-info">
              {parsedCards.filter((c) => c.blanks === 0).length} standalone,{' '}
              {parsedCards.filter((c) => c.blanks > 0).length} with blanks
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      {parsedCards.length > 0 && (
        <div className="form-group">
          <label>Preview (showing first 5):</label>
          <div className="bulk-preview">
            {parsedCards.slice(0, 5).map((card, index) => (
              <div
                key={index}
                className={`card-preview-small ${
                  type === 'black' ? 'question-card-preview' : 'answer-card-preview'
                }`}
              >
                {card.text}
                {type === 'black' && card.blanks > 0 && (
                  <span className="preview-blanks">
                    {' '}
                    [{card.blanks} blank{card.blanks !== 1 ? 's' : ''}]
                  </span>
                )}
              </div>
            ))}
            {parsedCards.length > 5 && (
              <div className="preview-more">... and {parsedCards.length - 5} more</div>
            )}
          </div>
        </div>
      )}

      {/* Errors */}
      {errors.length > 0 && (
        <div className="error-box">
          {errors.slice(0, 5).map((error, index) => (
            <div key={index} className="error-message">
              ❌ {error}
            </div>
          ))}
          {errors.length > 5 && (
            <div className="error-message">... and {errors.length - 5} more errors</div>
          )}
        </div>
      )}

      <div className="modal-hint">
        💡 Tip: Each line becomes a separate card. Empty lines are ignored.
      </div>
    </Modal>
  );
};

export default BulkAddCardsModal;
